import React, { createContext, useContext, useState, useEffect, type ReactNode } from "react";
import { toast } from "sonner";

interface BluetoothContextType {
  isBluetoothConnected: boolean;
  isConnecting: boolean;
  bluetoothDevice: BluetoothDevice | null;
  connectBluetooth: () => Promise<void>;
  disconnectBluetooth: () => Promise<void>;
  sendToPrinter: (data: Uint8Array) => Promise<void>;
}

const BluetoothContext = createContext<BluetoothContextType | undefined>(undefined);

export function useBluetooth() {
  const context = useContext(BluetoothContext);
  if (context === undefined) {
    throw new Error("useBluetooth must be used within a BluetoothProvider");
  }
  return context;
}

export function BluetoothProvider({ children }: { children: ReactNode }) {
  const [bluetoothDevice, setBluetoothDevice] = useState<BluetoothDevice | null>(null);
  const [isBluetoothConnected, setIsBluetoothConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [printerCharacteristic, setPrinterCharacteristic] = useState<BluetoothRemoteGATTCharacteristic | null>(null);

  const handleDisconnection = () => {
    setIsBluetoothConnected(false);
    setBluetoothDevice(null);
    setPrinterCharacteristic(null);
    toast.error("Imprimante Bluetooth déconnectée");
  };

  const disconnectBluetooth = async () => {
    if (bluetoothDevice) {
      try {
        await bluetoothDevice.gatt?.disconnect();
      } catch (error) {
        console.error("Erreur déconnexion:", error);
      }
    }
    setIsBluetoothConnected(false);
    setBluetoothDevice(null);
    setPrinterCharacteristic(null);
  };

  const connectBluetooth = async () => {
    if (isBluetoothConnected) {
      await disconnectBluetooth();
      toast.info("Déconnecté de l'imprimante");
      return;
    }

    if (!navigator.bluetooth) {
      toast.error("Le Bluetooth n'est pas supporté par ce navigateur.");
      return;
    }

    setIsConnecting(true);
    try {
      // Common thermal printer UUIDs
      const PRINTER_SERVICE_UUID = '000018f0-0000-1000-8000-00805f9b34fb';
      const PRINTER_CHARACTERISTIC_UUID = '00002af1-0000-1000-8000-00805f9b34fb';

      const device = await navigator.bluetooth.requestDevice({
        filters: [
          // Common thermal printer name patterns
          { namePrefix: 'RPP' },        // Rongta printers
          { namePrefix: 'MPT' },        // Mobile printers
          { namePrefix: 'PT-' },        // Brother printers
          { namePrefix: 'QL-' },        // Brother label printers
          { namePrefix: 'RW-' },        // Zebra printers
          { namePrefix: 'ZQ' },         // Zebra mobile printers
          { namePrefix: 'iMZ' },        // Zebra iMZ
          { namePrefix: 'SM-' },        // Star Micronics
          { namePrefix: 'TSP' },        // Star TSP
          { namePrefix: 'mPOP' },       // Star mPOP
          { namePrefix: 'MTP' },        // Printer generic
          { namePrefix: 'BlueTooth Printer' },
          { namePrefix: 'Bluetooth Printer' },
          { namePrefix: 'Printer' },
          { namePrefix: 'POS' },
          { namePrefix: 'ESC' },
          { namePrefix: 'Thermal' },
          { namePrefix: 'GOOJPRT' },
          { namePrefix: 'Gprinter' },
          { namePrefix: 'XP-' },        // Xprinter
          { namePrefix: 'HM-' },        // HPRT printers
          { namePrefix: 'SPP' },        // Bixolon printers
          { namePrefix: 'NIIMBOT' },    // Niimbot label printers
          { namePrefix: 'InnerPrinter' },
          { services: [PRINTER_SERVICE_UUID] },
        ],
        optionalServices: [PRINTER_SERVICE_UUID]
      });

      device.addEventListener('gattserverdisconnected', handleDisconnection);
      
      const server = await device.gatt?.connect();
      if (!server) throw new Error("Impossible de se connecter au serveur GATT");

      // Try to find the primary service and characteristic
      const services = await (server as any).getPrimaryServices();
      let characteristic: BluetoothRemoteGATTCharacteristic | null = null;

      for (const service of services) {
        const characteristics = await service.getCharacteristics();
        // Look for a characteristic that supports 'write'
        const writable = characteristics.find(c => c.properties.write || c.properties.writeWithoutResponse);
        if (writable) {
          characteristic = writable;
          break;
        }
      }

      if (!characteristic) {
          throw new Error("Aucune caractéristique d'écriture trouvée sur l'imprimante");
      }
      
      setBluetoothDevice(device);
      setPrinterCharacteristic(characteristic);
      setIsBluetoothConnected(true);
      toast.success(`Connecté à: ${device.name || "Imprimante"}`);
    } catch (error: any) {
      if (error.name === 'NotFoundError') {
        console.log("Sélection Bluetooth annulée");
      } else {
        console.error("Bluetooth error:", error);
        toast.error("Erreur de connexion Bluetooth : " + error.message);
      }
    } finally {
      setIsConnecting(false);
    }
  };

  const sendToPrinter = async (data: Uint8Array) => {
    if (!printerCharacteristic) {
      toast.error("L'imprimante n'est pas connectée");
      return;
    }

    try {
      // Use standard writeValue (with response) for maximum reliability with binary data (QR codes, etc.)
      // 100 bytes is a safe chunk size that reduces roundtrips while fitting within most hardware buffers.
      const CHUNK_SIZE = 100; 
      for (let i = 0; i < data.length; i += CHUNK_SIZE) {
        const chunk = data.slice(i, i + CHUNK_SIZE);
        await printerCharacteristic.writeValue(chunk);
        // Minimal delay between reliable writes
        await new Promise(r => setTimeout(r, 20));
      }
    } catch (error: any) {
      console.error("Erreur d'envoi à l'imprimante:", error);
      if (error.message?.includes("GATT") || error.message?.includes("disconnected")) {
        handleDisconnection();
      }
      toast.error("Erreur d'impression : " + error.message);
    }
  };

  return (
    <BluetoothContext.Provider value={{ 
      isBluetoothConnected, 
      isConnecting, 
      bluetoothDevice, 
      connectBluetooth, 
      disconnectBluetooth,
      sendToPrinter
    }}>
      {children}
    </BluetoothContext.Provider>
  );
}
